import {
  useRef,
  useState,
  useCallback,
  FC,
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
} from 'react';
import clsx from 'clsx';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { ProductDescType } from 'screens/AccessExpressV3/accessExpress.types';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
type AttributeType = {
  id: string;
  element: ReactNode;
};
interface AttributesProps {
  productDesc: ProductDescType;
  attributes: AttributeType[];
  onAddToCart: () => void;
  selected: string[];
}

const Attributes: FC<AttributesProps> = ({
  attributes,
  onAddToCart,
  selected,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const attributesRef = useRef<AttributeType[]>(attributes ?? []);
  const { state } = useLocation();
  const selectedState = state?.selected ?? null;

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, attributes.length);
    attributesRef.current = attributes;
  }, [attributes]);

  const handleStep = useCallback((step: number) => {
    setTimeout(() => {
      const newStep = step + 1;
      const attributes = attributesRef.current;
      if (newStep >= 0 && newStep < attributes.length) {
        setCurrentStep(newStep);
      }
    }, 0);
  }, []);

  const [isScrolling, setIsScrolling] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const onScroll = () => {
    if (!isScrolling) {
      setIsScrolling(true);
    }
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 600);
  };

  const [totalHeightAbove, setTotalHeightAbove] = useState(0);

  useEffect(() => {
    setTotalHeightAbove(() => {
      const _totalHeightAbove =
        sectionRefs.current
          .slice(0, currentStep)
          .reduce((sum, ref) => sum + (ref?.offsetHeight || 0), 0) -
        (scrollRef.current?.scrollTop ?? 0) +
        currentStep * 56 +
        currentStep * 36;
      return _totalHeightAbove;
    });
  }, [currentStep]);

  const wheelTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleWheel = (e: React.WheelEvent) => {
    if (e.deltaY < 0 && scrollRef.current?.scrollTop === 0) {
      if (wheelTimeout.current) return;
      setCurrentStep((prev) => {
        if (prev === 0) return 0;
        else {
          return prev - 1;
        }
      });
      onScroll();
      wheelTimeout.current = setTimeout(() => {
        wheelTimeout.current = null;
      }, 300);
    }
  };

  return (
    <div className="relative w-full rounded bg-transparent p-[1px] lg:w-[330px]">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          content: '""',
          position: 'absolute',
          inset: 0,
          borderRadius: 'inherit',
          padding: '1px',
          background:
            'linear-gradient(138.31deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 99.4%)',
          mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMask:
            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          maskComposite: 'exclude',
          WebkitMaskComposite: 'xor',
        }}
      />
      {/* <div
        style={{
          background:
            'linear-gradient(180deg, rgba(25, 25, 37, 0) 63.81%, #191925 124.81%)',
            boxShadow:"0px 4px 4px 0px #00000040"

        }}
        className="pointer-events-none absolute left-0 top-0 z-10 h-full w-full rounded shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
      /> */}
      <div className="relative flex h-[calc(100vh-206px)] w-full self-start rounded bg-navigation-surface-primary bg-opacity-[54%]  lg:h-[calc(100vh-270px)] xl:h-[calc(100vh-206px)]">
        <div className="flex h-full w-full flex-col overflow-hidden py-8">
          <div className="flex gap-1 px-6">
            {attributes.map((step, index) => (
              <div
                key={step.id}
                className={clsx(
                  'h-1.5 w-full rounded-sm',
                  selected.includes(step.id) || index === currentStep
                    ? 'bg-accent-primary'
                    : 'bg-neutral-700',
                )}
              />
            ))}
          </div>
          <div
            className={clsx(
              '-mr-2 overflow-y-scroll pr-2 scrollbar-thin scrollbar-track-transparent hover:-mr-[0px] hover:pr-[0px]',
              {
                'scrollbar-thumb-stroke-base-subdued': isScrolling,
                'scrollbar-thumb-transparent': !isScrolling,
              },
            )}
            ref={scrollRef}
            onScroll={onScroll}
            onWheel={handleWheel}
          >
            <motion.div
              transition={{
                type: 'spring',
                mass: 1,
                stiffness: 60,
                damping: 20.3,
                duration: 0.591,
              }}
              animate={{ top: `-${totalHeightAbove}px` }}
              className="relative flex h-full flex-col gap-9 "
            >
              {attributes.map((att, index) => (
                <div
                  key={att.id}
                  ref={(el) => (sectionRefs.current[index] = el)}
                  className={clsx(
                    'mt-14 w-full flex-shrink-0 transition-opacity duration-[600ms] xl:min-h-[calc(100%-270px)]',
                    currentStep === index || selected.includes(att.id)
                      ? 'relative z-10'
                      : 'pointer-events-none',
                    {
                      'opacity-50':
                        currentStep !== index && !selected.includes(att.id),
                    },
                  )}
                >
                  {cloneElement(
                    isValidElement(att.element) ? (
                      att.element
                    ) : (
                      <>{att.element}</>
                    ),
                    { handleStep, step: index },
                  )}
                  {index === attributes.length - 1 && (
                    <div className="px-6">
                      <GraniteButton
                        onClick={onAddToCart}
                        variant="tertiary"
                        className="mb-1 mt-8 w-full"
                      >
                        {selectedState ? 'Save changes' : 'Add to cart'}
                      </GraniteButton>
                    </div>
                  )}
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attributes;
